import React, { useState, useEffect, useCallback } from "react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine,
} from "recharts";
import {
  Play, Square, Radio, TrendingUp, TrendingDown, Activity, BarChart3,
  ArrowUpRight, ArrowDownRight, Clock, DollarSign, Target, Shield,
  Zap, AlertTriangle, CheckCircle2, RefreshCw,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

import { API_BASE as API } from '../config';

function getToken() {
  return localStorage.getItem("token");
}

function headers() {
  return { Authorization: `Bearer ${getToken()}` };
}

export default function LivePaperPage() {
  const [status, setStatus] = useState(null);
  const [trades, setTrades] = useState([]);
  const [equity, setEquity] = useState([]);
  const [comparison, setComparison] = useState(null);
  const [regimeData, setRegimeData] = useState(null);
  const [simData, setSimData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [deploying, setDeploying] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [config, setConfig] = useState({
    capital: 500,
    risk_per_trade_pct: 1.0,
    daily_loss_limit_pct: 3.0,
    hard_stop_pct: 20.0,
    losing_streak_pause: 5,
  });

  const fetchAll = useCallback(async () => {
    try {
      const [statusRes, tradesRes, equityRes, compRes] = await Promise.all([
        axios.get(`${API}/api/live-paper/status`, { headers: headers() }),
        axios.get(`${API}/api/live-paper/trades?limit=100`, { headers: headers() }),
        axios.get(`${API}/api/live-paper/equity`, { headers: headers() }),
        axios.get(`${API}/api/live-paper/comparison`, { headers: headers() }),
      ]);
      setStatus(statusRes.data);
      setTrades(tradesRes.data.trades || []);
      setEquity(equityRes.data.equity_curve || []);
      setComparison(compRes.data);
    } catch (e) {
      console.error("Fetch error:", e);
    }
  }, []);

  // Fetch regime analysis + simulation once (cached data)
  useEffect(() => {
    const fetchCached = async () => {
      try {
        const [regimeRes, simRes] = await Promise.all([
          axios.get(`${API}/api/live-paper/regime-analysis?strategy_name=ATR_RSI_m20lb8r55_SL20`, { headers: headers() }).catch(() => null),
          axios.get(`${API}/api/live-paper/regime-simulation`, { headers: headers() }).catch(() => null),
        ]);
        if (regimeRes?.data?.regime_results) setRegimeData(regimeRes.data);
        if (simRes?.data?.simulations) setSimData(simRes.data.simulations);
      } catch (e) {
        console.error("Cached data fetch error:", e);
      }
    };
    fetchCached();
  }, []);

  useEffect(() => {
    fetchAll();
    const interval = setInterval(fetchAll, 15000);
    return () => clearInterval(interval);
  }, [fetchAll]);

  const handleDeploy = async () => {
    setDeploying(true);
    try {
      await axios.post(`${API}/api/live-paper/deploy`, {
        strategy_name: "ATR_VOL_m20lb14v15_SL20",
        symbol: "ETHUSDC",
        timeframe: "4h",
        ...config,
      }, { headers: headers() });
      toast.success("Live Paper deployed — ATR_VOL_m20lb14v15_SL20");
      await fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Deploy failed");
    } finally {
      setDeploying(false);
    }
  };

  const handleStop = async () => {
    try {
      await axios.post(`${API}/api/live-paper/stop`, {}, { headers: headers() });
      toast.success("Live Paper stopped");
      await fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Stop failed");
    }
  };

  const handleUpdateConfig = async () => {
    try {
      const r = await axios.patch(`${API}/api/live-paper/config`, config, { headers: headers() });
      toast.success("Config updated: " + (r.data.changes || []).join(", "));
      setShowSettings(false);
      await fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    }
  };

  const isRunning = status?.is_running;
  const pos = status?.open_position;
  const bt = status?.backtest_profile || {};

  return (
    <div className="space-y-4" data-testid="live-paper-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-primary/10 border border-primary/30 flex items-center justify-center">
            <Radio className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-heading font-bold tracking-tight">LIVE PAPER</h1>
            <p className="text-xs text-muted-foreground font-mono">
              ATR_VOL_m20lb14v15_SL20 | ETHUSDC 4H | HYBRID 70/30
            </p>
          </div>
          {isRunning && (
            <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30 animate-pulse ml-2" data-testid="live-status-badge">
              LIVE
            </Badge>
          )}
        </div>
        <div className="flex gap-2">
          <Button variant="ghost" size="sm" onClick={() => setShowSettings(!showSettings)} data-testid="settings-btn">
            <Target className="w-4 h-4" />
          </Button>
          <Button variant="ghost" size="sm" onClick={fetchAll} data-testid="refresh-btn">
            <RefreshCw className="w-4 h-4" />
          </Button>
          {!isRunning ? (
            <Button
              onClick={handleDeploy}
              disabled={deploying}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="deploy-btn"
            >
              <Play className="w-4 h-4 mr-2" />
              {deploying ? "Deploying..." : "Deploy Champion"}
            </Button>
          ) : (
            <Button
              onClick={handleStop}
              variant="destructive"
              data-testid="stop-btn"
            >
              <Square className="w-4 h-4 mr-2" />
              Stop
            </Button>
          )}
        </div>
      </div>

      {/* Settings Panel */}
      {showSettings && (
        <Card className="border-primary/30 bg-zinc-900/60" data-testid="settings-panel">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-bold text-zinc-300 tracking-wider flex items-center gap-2">
                <Target className="w-4 h-4 text-primary" /> TRADING CONFIG
              </h3>
              <span className="text-[10px] text-zinc-500">Change live — no redeploy needed</span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <div>
                <label className="text-[10px] text-zinc-500 block mb-1">Capital ($)</label>
                <input
                  type="number"
                  value={config.capital}
                  onChange={e => setConfig(c => ({...c, capital: +e.target.value}))}
                  className="w-full h-8 bg-zinc-800 border border-zinc-700 rounded px-2 text-sm text-zinc-200 font-mono"
                  data-testid="config-capital"
                />
              </div>
              <div>
                <label className="text-[10px] text-zinc-500 block mb-1">Risk/Trade (%)</label>
                <input
                  type="number"
                  step="0.1"
                  value={config.risk_per_trade_pct}
                  onChange={e => setConfig(c => ({...c, risk_per_trade_pct: +e.target.value}))}
                  className="w-full h-8 bg-zinc-800 border border-zinc-700 rounded px-2 text-sm text-zinc-200 font-mono"
                  data-testid="config-risk"
                />
              </div>
              <div>
                <label className="text-[10px] text-zinc-500 block mb-1">Daily Stop (%)</label>
                <input
                  type="number"
                  step="0.5"
                  value={config.daily_loss_limit_pct}
                  onChange={e => setConfig(c => ({...c, daily_loss_limit_pct: +e.target.value}))}
                  className="w-full h-8 bg-zinc-800 border border-zinc-700 rounded px-2 text-sm text-zinc-200 font-mono"
                  data-testid="config-daily-stop"
                />
              </div>
              <div>
                <label className="text-[10px] text-zinc-500 block mb-1">Kill Switch (%)</label>
                <input
                  type="number"
                  step="1"
                  value={config.hard_stop_pct}
                  onChange={e => setConfig(c => ({...c, hard_stop_pct: +e.target.value}))}
                  className="w-full h-8 bg-zinc-800 border border-zinc-700 rounded px-2 text-sm text-zinc-200 font-mono"
                  data-testid="config-kill"
                />
              </div>
              <div>
                <label className="text-[10px] text-zinc-500 block mb-1">Streak Pause</label>
                <input
                  type="number"
                  value={config.losing_streak_pause}
                  onChange={e => setConfig(c => ({...c, losing_streak_pause: +e.target.value}))}
                  className="w-full h-8 bg-zinc-800 border border-zinc-700 rounded px-2 text-sm text-zinc-200 font-mono"
                  data-testid="config-streak"
                />
              </div>
            </div>
            <div className="flex justify-end mt-3 gap-2">
              <Button variant="ghost" size="sm" onClick={() => setShowSettings(false)} className="text-zinc-500">Cancel</Button>
              <Button size="sm" onClick={handleUpdateConfig} className="bg-primary hover:bg-primary/80 text-white" data-testid="save-config-btn">
                Save Changes
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Top Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        <StatCard
          label="Balance"
          value={`$${(status?.balance || 10000).toLocaleString(undefined, { minimumFractionDigits: 2 })}`}
          icon={DollarSign}
          testId="stat-balance"
        />
        <StatCard
          label="Total P&L"
          value={`${status?.total_pnl >= 0 ? '+' : ''}$${(status?.total_pnl || 0).toFixed(2)}`}
          sub={`${status?.total_pnl_pct >= 0 ? '+' : ''}${(status?.total_pnl_pct || 0).toFixed(2)}%`}
          color={status?.total_pnl >= 0 ? "text-emerald-400" : "text-red-400"}
          icon={status?.total_pnl >= 0 ? TrendingUp : TrendingDown}
          testId="stat-pnl"
        />
        <StatCard
          label="Win Rate"
          value={`${(status?.win_rate || 0).toFixed(1)}%`}
          sub={`${status?.wins || 0}W / ${status?.losses || 0}L`}
          icon={Target}
          testId="stat-winrate"
        />
        <StatCard
          label="Max Drawdown"
          value={`${(status?.max_drawdown_pct || 0).toFixed(2)}%`}
          color="text-orange-400"
          icon={Shield}
          testId="stat-drawdown"
        />
        <StatCard
          label="Total Trades"
          value={status?.total_trades || 0}
          icon={BarChart3}
          testId="stat-trades"
        />
        <StatCard
          label="Fees + Slippage"
          value={`$${((status?.total_fees || 0) + (status?.total_slippage || 0)).toFixed(2)}`}
          sub={`Fee: $${(status?.total_fees || 0).toFixed(2)} | Slip: $${(status?.total_slippage || 0).toFixed(2)}`}
          color="text-yellow-400"
          icon={Zap}
          testId="stat-fees"
        />
      </div>

      {/* Regime Firewall Panel */}
      {isRunning && status?.regime_firewall && (
        <Card className="bg-card border-border" data-testid="regime-firewall-panel">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Shield className="w-4 h-4 text-muted-foreground" />
                <h3 className="text-sm font-heading font-semibold uppercase tracking-wide text-muted-foreground">
                  Regime Firewall
                </h3>
              </div>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-4 text-xs font-mono text-muted-foreground">
                  <span>Passed: <span className="text-emerald-400">{status.regime_firewall.signals_passed}</span></span>
                  <span>Throttled: <span className="text-amber-400">{status.regime_firewall.signals_throttled}</span></span>
                  <span>Blocked: <span className="text-red-400">{status.regime_firewall.signals_blocked}</span></span>
                </div>
                <Badge
                  className={`text-xs font-mono ${
                    status.regime_firewall.execution_status === "ACTIVE" ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/30" :
                    status.regime_firewall.execution_status === "THROTTLED" ? "bg-amber-500/20 text-amber-400 border-amber-500/30" :
                    "bg-red-500/20 text-red-400 border-red-500/30"
                  }`}
                  data-testid="firewall-status-badge"
                >
                  {status.regime_firewall.execution_status}
                </Badge>
              </div>
            </div>
            <div className="mt-3 flex items-center gap-6">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: status.regime_firewall.regime_color }} />
                <span className="text-sm font-semibold" data-testid="current-regime-label">
                  {status.regime_firewall.regime_label}
                </span>
              </div>
              <div className="text-xs text-muted-foreground font-mono">
                Confidence: <span className="text-foreground">{(status.regime_firewall.confidence * 100).toFixed(0)}%</span>
              </div>
              <div className="text-xs text-muted-foreground font-mono">
                Size: <span className="text-foreground">{(status.regime_firewall.size_multiplier * 100).toFixed(0)}%</span>
              </div>
              <div className="flex-1 text-xs text-muted-foreground text-right">
                {status.regime_firewall.reason}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Main Grid: Equity + Position */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Equity Curve */}
        <Card className="lg:col-span-2 bg-card border-border">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-heading font-semibold uppercase tracking-wide text-muted-foreground">
                Live Equity Curve
              </h3>
              {status?.live_apy ? (
                <Badge variant="outline" className="text-xs font-mono">
                  Live APY: {status.live_apy.toFixed(1)}%
                </Badge>
              ) : null}
            </div>
            <div className="h-56" data-testid="equity-chart">
              {equity.length > 1 ? (
                <ResponsiveContainer>
                  <LineChart data={equity}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(0 0% 15%)" />
                    <XAxis
                      dataKey="ts"
                      tick={{ fontSize: 10, fill: "#666" }}
                      tickFormatter={(v) => v ? new Date(v).toLocaleDateString("en", { month: "short", day: "numeric" }) : ""}
                    />
                    <YAxis
                      tick={{ fontSize: 10, fill: "#666" }}
                      tickFormatter={(v) => `$${v.toLocaleString()}`}
                      domain={["auto", "auto"]}
                    />
                    <Tooltip
                      contentStyle={{ background: "hsl(0 0% 7%)", border: "1px solid hsl(0 0% 15%)", fontSize: 12 }}
                      formatter={(v) => [`$${Number(v).toFixed(2)}`, "Equity"]}
                      labelFormatter={(v) => v ? new Date(v).toLocaleString() : ""}
                    />
                    <ReferenceLine y={status?.capital || 10000} stroke="#666" strokeDasharray="3 3" label={{ value: "Capital", fill: "#666", fontSize: 10 }} />
                    <Line
                      type="monotone"
                      dataKey="equity"
                      stroke="#00ff88"
                      strokeWidth={2}
                      dot={false}
                      activeDot={{ r: 4 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center text-muted-foreground text-sm">
                  {isRunning ? "Waiting for first trade..." : "Deploy the champion to start tracking"}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Position + Signal Panel */}
        <div className="space-y-4">
          <Card className="bg-card border-border">
            <CardContent className="p-4">
              <h3 className="text-sm font-heading font-semibold uppercase tracking-wide text-muted-foreground mb-3">
                Current Position
              </h3>
              {pos ? (
                <div className="space-y-2" data-testid="position-panel">
                  <div className="flex items-center justify-between">
                    <Badge className={pos.side === "LONG" ? "bg-emerald-500/20 text-emerald-400" : "bg-red-500/20 text-red-400"}>
                      {pos.side}
                    </Badge>
                    <span className="text-xs text-muted-foreground font-mono">{pos.id}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div>
                      <div className="text-muted-foreground">Entry</div>
                      <div className="font-mono font-semibold">${pos.entry_price?.toLocaleString()}</div>
                    </div>
                    <div>
                      <div className="text-muted-foreground">Quantity</div>
                      <div className="font-mono">{pos.quantity?.toFixed(4)}</div>
                    </div>
                    <div>
                      <div className="text-muted-foreground">SL</div>
                      <div className="font-mono text-red-400">${pos.sl_price?.toLocaleString()} ({pos.sl_pct}%)</div>
                    </div>
                    <div>
                      <div className="text-muted-foreground">TP</div>
                      <div className="font-mono text-emerald-400">${pos.tp_price?.toLocaleString()} ({pos.tp_pct}%)</div>
                    </div>
                    <div>
                      <div className="text-muted-foreground">Notional</div>
                      <div className="font-mono">${pos.notional?.toLocaleString()}</div>
                    </div>
                    <div>
                      <div className="text-muted-foreground">Unrealized</div>
                      <div className={`font-mono font-semibold ${(pos.unrealized_pnl || 0) >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                        ${(pos.unrealized_pnl || 0).toFixed(2)}
                      </div>
                    </div>
                  </div>
                  <div className="text-[10px] text-muted-foreground mt-1">
                    Opened: {new Date(pos.opened_at).toLocaleString()}
                  </div>
                </div>
              ) : (
                <div className="text-center py-6 text-muted-foreground text-xs" data-testid="no-position">
                  No open position
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="bg-card border-border">
            <CardContent className="p-4">
              <h3 className="text-sm font-heading font-semibold uppercase tracking-wide text-muted-foreground mb-3">
                Last Signal
              </h3>
              {status?.last_signal ? (
                <div className="space-y-1 text-xs" data-testid="signal-panel">
                  <div className="flex items-center gap-2">
                    <Badge className={status.last_signal.side === "LONG" ? "bg-emerald-500/20 text-emerald-400" : "bg-red-500/20 text-red-400"}>
                      {status.last_signal.side}
                    </Badge>
                    <span className="font-mono text-muted-foreground">{status.last_signal.name}</span>
                  </div>
                  <div className="text-muted-foreground">
                    SL: {(status.last_signal.sl_pct * 100).toFixed(1)}% | TP: {(status.last_signal.tp_pct * 100).toFixed(1)}%
                  </div>
                  <div className="text-muted-foreground text-[10px]">
                    {status.last_signal_time ? new Date(status.last_signal_time).toLocaleString() : ""}
                  </div>
                </div>
              ) : (
                <div className="text-center py-4 text-muted-foreground text-xs" data-testid="no-signal">
                  {isRunning ? "Scanning for signals..." : "Deploy to start scanning"}
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="bg-card border-border">
            <CardContent className="p-4">
              <h3 className="text-sm font-heading font-semibold uppercase tracking-wide text-muted-foreground mb-2">
                Session Info
              </h3>
              <div className="space-y-1.5 text-xs">
                <InfoRow label="Strategy" value={status?.strategy_name || "—"} />
                <InfoRow label="Status" value={isRunning ? "RUNNING" : "STOPPED"} color={isRunning ? "text-emerald-400" : "text-muted-foreground"} />
                <InfoRow label="Days Running" value={`${status?.days_running || 0}d`} />
                <InfoRow label="Ticks" value={status?.tick_count || 0} />
                <InfoRow label="Profit Factor" value={(status?.profit_factor || 0).toFixed(2)} />
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Backtest Comparison Panel */}
      {comparison && (
        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <h3 className="text-sm font-heading font-semibold uppercase tracking-wide text-muted-foreground mb-3">
              Live vs Backtest Comparison
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4" data-testid="comparison-panel">
              <CompareMetric label="APY" bt={`${bt.apy_pct}%`} live={`${comparison.live?.apy_pct?.toFixed(1) || 0}%`} delta={comparison.deltas?.apy_delta} />
              <CompareMetric label="Win Rate" bt={`${bt.win_rate}%`} live={`${comparison.live?.win_rate?.toFixed(1) || 0}%`} delta={comparison.deltas?.wr_delta} />
              <CompareMetric label="Max DD" bt={`${bt.max_dd_pct}%`} live={`${comparison.live?.max_dd_pct?.toFixed(2) || 0}%`} delta={comparison.deltas?.dd_delta} invert />
              <CompareMetric label="Profit Factor" bt={bt.profit_factor} live={comparison.live?.profit_factor?.toFixed(2) || 0} delta={comparison.deltas?.pf_delta} />
              <CompareMetric label="Trades" bt={bt.total_trades} live={comparison.live?.total_trades || 0} />
              <CompareMetric label="Total Fees" bt="—" live={`$${comparison.live?.total_fees?.toFixed(2) || 0}`} />
              <div className="flex flex-col items-center justify-center">
                <div className="text-[10px] text-muted-foreground mb-1">Verdict</div>
                <VerdictBadge verdict={comparison.verdict} />
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Trade Log */}
      <Card className="bg-card border-border">
        <CardContent className="p-4">
          <h3 className="text-sm font-heading font-semibold uppercase tracking-wide text-muted-foreground mb-3">
            Entry / Exit Log
          </h3>
          {trades.length > 0 ? (
            <div className="overflow-x-auto" data-testid="trade-log">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-border text-muted-foreground text-left">
                    <th className="pb-2 pr-3">#</th>
                    <th className="pb-2 pr-3">Side</th>
                    <th className="pb-2 pr-3">Entry</th>
                    <th className="pb-2 pr-3">Exit</th>
                    <th className="pb-2 pr-3">Reason</th>
                    <th className="pb-2 pr-3">Fees</th>
                    <th className="pb-2 pr-3">Slip</th>
                    <th className="pb-2 pr-3">Net P&L</th>
                    <th className="pb-2">Time</th>
                  </tr>
                </thead>
                <tbody>
                  {trades.map((t, i) => (
                    <tr key={t.id || i} className="border-b border-border/30 hover:bg-muted/20">
                      <td className="py-1.5 pr-3 text-muted-foreground">{trades.length - i}</td>
                      <td className="py-1.5 pr-3">
                        <span className={t.side === "LONG" ? "text-emerald-400" : "text-red-400"}>{t.side}</span>
                      </td>
                      <td className="py-1.5 pr-3 font-mono">${t.entry_price?.toLocaleString()}</td>
                      <td className="py-1.5 pr-3 font-mono">${t.exit_price?.toLocaleString()}</td>
                      <td className="py-1.5 pr-3">
                        <Badge variant="outline" className={`text-[10px] ${t.exit_reason === "TAKE_PROFIT" ? "border-emerald-500/30 text-emerald-400" : t.exit_reason === "STOP_LOSS" ? "border-red-500/30 text-red-400" : "border-border"}`}>
                          {t.exit_reason}
                        </Badge>
                      </td>
                      <td className="py-1.5 pr-3 font-mono text-yellow-400">${t.total_fees?.toFixed(2)}</td>
                      <td className="py-1.5 pr-3 font-mono text-yellow-400">${t.total_slippage?.toFixed(2)}</td>
                      <td className={`py-1.5 pr-3 font-mono font-semibold ${t.net_pnl >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                        {t.net_pnl >= 0 ? "+" : ""}${t.net_pnl?.toFixed(2)}
                      </td>
                      <td className="py-1.5 text-muted-foreground text-[10px]">
                        {t.closed_at ? new Date(t.closed_at).toLocaleString("en", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : ""}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground text-sm" data-testid="no-trades">
              No trades yet. {isRunning ? "Waiting for signals..." : "Deploy the champion to start."}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Regime Analysis Panel */}
      {regimeData && regimeData.regime_results && (
        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-heading font-semibold uppercase tracking-wide text-muted-foreground">
                Regime Analysis — 8.6 Year History
              </h3>
              <div className="flex items-center gap-2 text-xs font-mono text-muted-foreground">
                <span>{regimeData.total_trades} trades</span>
                <span>|</span>
                <span className="text-emerald-400">+{regimeData.total_return_pct?.toLocaleString()}%</span>
                <span>|</span>
                <span>${regimeData.final_balance?.toLocaleString()}</span>
              </div>
            </div>
            <div data-testid="regime-analysis-panel">
              {/* Regime bars */}
              <div className="space-y-2 mb-4">
                {regimeData.regime_results.map((r) => {
                  const maxPnl = Math.max(...regimeData.regime_results.map(x => Math.abs(x.total_pnl)));
                  const barWidth = maxPnl > 0 ? Math.abs(r.total_pnl) / maxPnl * 100 : 0;
                  const isPositive = r.total_pnl >= 0;
                  return (
                    <div key={r.regime} className="flex items-center gap-3">
                      <div className="w-32 text-xs font-mono flex items-center gap-1.5">
                        <div className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: r.color }} />
                        {r.label}
                      </div>
                      <div className="flex-1 h-5 bg-muted/20 relative overflow-hidden">
                        <div
                          className={`h-full ${isPositive ? "bg-emerald-500/40" : "bg-red-500/40"}`}
                          style={{ width: `${barWidth}%` }}
                        />
                        <div className="absolute inset-0 flex items-center px-2 text-[10px] font-mono font-semibold">
                          <span className={isPositive ? "text-emerald-400" : "text-red-400"}>
                            ${r.total_pnl?.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                          </span>
                        </div>
                      </div>
                      <div className="w-48 flex gap-2 text-[10px] font-mono text-muted-foreground">
                        <span>{r.trades}T</span>
                        <span>WR:{r.win_rate}%</span>
                        <span>PF:{r.profit_factor}</span>
                        <span>{r.pct_of_time}%</span>
                      </div>
                    </div>
                  );
                })}
              </div>
              {/* Key findings */}
              <div className="grid grid-cols-3 gap-3 pt-3 border-t border-border">
                <div className="text-center">
                  <div className="text-[10px] text-muted-foreground mb-1">Best Regime</div>
                  <div className="text-xs font-mono font-semibold text-emerald-400">{regimeData.best_regime?.regime}</div>
                  <div className="text-[10px] text-muted-foreground">${regimeData.best_regime?.pnl?.toLocaleString()} | {regimeData.best_regime?.win_rate}% WR</div>
                </div>
                <div className="text-center">
                  <div className="text-[10px] text-muted-foreground mb-1">Most Explosive</div>
                  <div className="text-xs font-mono font-semibold text-amber-400">{regimeData.explosive_regime?.regime}</div>
                  <div className="text-[10px] text-muted-foreground">${regimeData.explosive_regime?.avg_pnl_per_trade?.toLocaleString()}/trade</div>
                </div>
                <div className="text-center">
                  <div className="text-[10px] text-muted-foreground mb-1">Danger Zone</div>
                  <div className="text-xs font-mono font-semibold text-red-400">{regimeData.worst_regime?.regime}</div>
                  <div className="text-[10px] text-muted-foreground">${regimeData.worst_regime?.pnl?.toLocaleString()} | {regimeData.worst_regime?.win_rate}% WR</div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Regime Firewall Simulation Comparison */}
      {simData && simData.length > 0 && (
        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <h3 className="text-sm font-heading font-semibold uppercase tracking-wide text-muted-foreground mb-3">
              Firewall Simulation — 8.6 Year Comparison
            </h3>
            <div className="overflow-x-auto" data-testid="simulation-panel">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-border text-muted-foreground text-left">
                    <th className="pb-2 pr-4">Variant</th>
                    <th className="pb-2 pr-4">Return</th>
                    <th className="pb-2 pr-4">MaxDD</th>
                    <th className="pb-2 pr-4">PF</th>
                    <th className="pb-2 pr-4">WR%</th>
                    <th className="pb-2 pr-4">Trades</th>
                    <th className="pb-2 pr-4">Blocked</th>
                    <th className="pb-2">Final $</th>
                  </tr>
                </thead>
                <tbody>
                  {simData.map((sim, i) => {
                    const isBase = i === 0;
                    const baseDd = simData[0]?.max_dd_pct || 0;
                    const ddImproved = !isBase && sim.max_dd_pct < baseDd;
                    const retImproved = !isBase && sim.total_return_pct > simData[0]?.total_return_pct;
                    return (
                      <tr key={sim.label} className={`border-b border-border/30 ${isBase ? "bg-muted/10" : "hover:bg-muted/10"}`}>
                        <td className="py-2 pr-4 font-mono font-semibold">
                          {sim.label}
                          {!isBase && sim.filters?.length > 0 && (
                            <span className="ml-1 text-[10px] text-red-400">
                              [{sim.filters.join(", ")}]
                            </span>
                          )}
                        </td>
                        <td className={`py-2 pr-4 font-mono font-bold ${retImproved ? "text-emerald-400" : isBase ? "text-foreground" : "text-muted-foreground"}`}>
                          +{sim.total_return_pct?.toLocaleString()}%
                        </td>
                        <td className={`py-2 pr-4 font-mono ${ddImproved ? "text-emerald-400" : "text-foreground"}`}>
                          {sim.max_dd_pct?.toFixed(2)}%
                          {ddImproved && <span className="text-emerald-400 text-[10px] ml-1">(-{(baseDd - sim.max_dd_pct).toFixed(1)}%)</span>}
                        </td>
                        <td className="py-2 pr-4 font-mono">{sim.profit_factor?.toFixed(2)}</td>
                        <td className="py-2 pr-4 font-mono">{sim.win_rate?.toFixed(1)}%</td>
                        <td className="py-2 pr-4 font-mono">{sim.total_trades}</td>
                        <td className="py-2 pr-4 font-mono text-red-400">{sim.blocked_signals}</td>
                        <td className="py-2 font-mono">${sim.final_balance?.toLocaleString()}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="mt-3 pt-3 border-t border-border text-xs text-muted-foreground">
              Removing High Volatility: <span className="text-emerald-400 font-semibold">+673% more return AND -3.34% drawdown</span>. The firewall increases gains and reduces risk.
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

/* ── Helper Components ── */

const StatCard = ({ label, value, sub, color, icon: Icon, testId }) => (
  <Card className="bg-card border-border">
    <CardContent className="p-3">
      <div className="flex items-center justify-between mb-1">
        <span className="text-[10px] text-muted-foreground uppercase tracking-wide">{label}</span>
        {Icon && <Icon className="w-3.5 h-3.5 text-muted-foreground" />}
      </div>
      <div className={`text-lg font-mono font-bold ${color || "text-foreground"}`} data-testid={testId}>
        {value}
      </div>
      {sub && <div className="text-[10px] text-muted-foreground font-mono mt-0.5">{sub}</div>}
    </CardContent>
  </Card>
);

const InfoRow = ({ label, value, color }) => (
  <div className="flex justify-between">
    <span className="text-muted-foreground">{label}</span>
    <span className={`font-mono ${color || "text-foreground"}`}>{value}</span>
  </div>
);

const CompareMetric = ({ label, bt, live, delta, invert }) => {
  const isGood = invert ? (delta || 0) <= 0 : (delta || 0) >= 0;
  return (
    <div className="text-center">
      <div className="text-[10px] text-muted-foreground mb-1">{label}</div>
      <div className="flex items-center justify-center gap-3 text-xs">
        <div>
          <div className="text-[9px] text-muted-foreground">Backtest</div>
          <div className="font-mono font-semibold">{bt}</div>
        </div>
        <div className="text-muted-foreground">vs</div>
        <div>
          <div className="text-[9px] text-primary">Live</div>
          <div className="font-mono font-semibold text-primary">{live}</div>
        </div>
      </div>
      {delta !== undefined && delta !== null && (
        <div className={`text-[10px] font-mono mt-0.5 ${isGood ? "text-emerald-400" : "text-red-400"}`}>
          {delta >= 0 ? "+" : ""}{typeof delta === 'number' ? delta.toFixed(1) : delta}
        </div>
      )}
    </div>
  );
};

const VerdictBadge = ({ verdict }) => {
  const config = {
    ON_TRACK: { color: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30", icon: CheckCircle2, label: "On Track" },
    MONITORING: { color: "bg-blue-500/20 text-blue-400 border-blue-500/30", icon: Activity, label: "Monitoring" },
    DECAY_DETECTED: { color: "bg-red-500/20 text-red-400 border-red-500/30", icon: AlertTriangle, label: "Decay!" },
    INSUFFICIENT_DATA: { color: "bg-zinc-500/20 text-zinc-400 border-zinc-500/30", icon: Clock, label: "Need Data" },
  };
  const c = config[verdict] || config.INSUFFICIENT_DATA;
  const Icon = c.icon;
  return (
    <Badge className={`${c.color} text-xs`} data-testid="verdict-badge">
      <Icon className="w-3 h-3 mr-1" />
      {c.label}
    </Badge>
  );
};
